import type { DataNode } from '../types/types'

/**
 * Insert node after sibling in array, or append if sibling not found.
 * @param arr - Source array to insert into
 * @param newNode - Node to insert
 * @param afterId - Key of sibling to insert after (appends if not found)
 * @returns New array with node inserted
 */
export const insertAfterSibling = (
    arr: DataNode[],
    newNode: DataNode,
    afterId?: string
): DataNode[] => {
    const idx = afterId ? arr.findIndex((n) => n.key === afterId) : -1
    const insertPos = idx === -1 ? arr.length : idx + 1
    return [...arr.slice(0, insertPos), newNode, ...arr.slice(insertPos)]
}

/**
 * Recursively find parent and insert child node.
 * @param tree - Tree to search
 * @param parentId - Key of parent node to insert into
 * @param newNode - Node to insert as child
 * @param afterId - Key of sibling to insert after within parent's children
 * @returns New tree with node inserted, or null if parent not found
 */
export const insertIntoParent = (
    tree: DataNode[],
    parentId: string,
    newNode: DataNode,
    afterId?: string
): DataNode[] | null => {
    for (let i = 0; i < tree.length; i++) {
        const node = tree[i]

        if (node.key === parentId) {
            return [
                ...tree.slice(0, i),
                { ...node, children: insertAfterSibling(node.children || [], newNode, afterId) },
                ...tree.slice(i + 1),
            ]
        }

        if (node.children?.length) {
            const result = insertIntoParent(node.children, parentId, newNode, afterId)
            if (result) {
                return [
                    ...tree.slice(0, i),
                    { ...node, children: result },
                    ...tree.slice(i + 1),
                ]
            }
        }
    }
    return null
}

/**
 * Recursively find and remove node from tree by key.
 * @param tree - Tree to search
 * @param keyToRemove - Key of node to remove
 * @returns New tree with node removed, or null if node not found
 */
export const removeFromTree = (
    tree: DataNode[],
    keyToRemove: string
): DataNode[] | null => {
    for (let i = 0; i < tree.length; i++) {
        const node = tree[i]

        // Found at this level - remove it
        if (node.key === keyToRemove) {
            return [...tree.slice(0, i), ...tree.slice(i + 1)]
        }

        // Search in children
        if (node.children?.length) {
            const result = removeFromTree(node.children, keyToRemove)
            if (result) {
                return [
                    ...tree.slice(0, i),
                    { ...node, children: result },
                    ...tree.slice(i + 1),
                ]
            }
        }
    }
    return null
}
